import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SortingState, Updater } from '@tanstack/react-table';
import { fetchCampaignRefunds } from '~/services/campaign';
import RefundsTable, {
    REFUNDS_PAGE_SIZE,
} from '~/components/Contributions/RefundsTable';

type Props = {
    ntafId: string;
    filters: { from: string; to: string; search: string };
};

/**
 * The "Refunded" view of the campaign donor list (HHLTB-2601).
 *
 * Fully refunded donations are deleted from hhlsync_loc, so they can't come
 * from the supporters endpoint — this reads the refund records instead, which
 * covers full and partial refunds uniformly. Kept separate from the donations
 * table rather than interleaved: the two come from different databases, so
 * merging them would mean paginating in Python across donor lists that run to
 * tens of thousands of rows.
 *
 * Every status is shown, with the refund-status column on to tell them apart
 * (HHLTB-2622). It used to be succeeded-only, which read as "no refunds
 * recorded" for a refund Stripe had not confirmed yet. That column is the
 * Stripe side of a refund and is deliberately separate from RE + MIP, which
 * tracks the hand-entry finance does afterwards.
 */
const RefundedDonationsTable: React.FC<Props> = ({ ntafId, filters }) => {
    const [page, setPage] = useState<number>(1);
    const [sorting, setSorting] = useState<SortingState>([]);

    // Ordered in the database so a sort covers every refund on the campaign,
    // not the twenty rows on the page in hand (HHLTB-2654).
    const sortParams = useMemo(() => {
        const [first] = sorting;
        if (!first) return {};
        return { sort_by: first.id, sort_dir: first.desc ? 'desc' : 'asc' };
    }, [sorting]);

    // A new sort reorders the whole result set, so the old page number is
    // meaningless — go back to the first page of the new order.
    const handleSortingChange = (updater: Updater<SortingState>) => {
        setPage(1);
        setSorting(updater);
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: ['campaign-refunds', ntafId, page, filters, sortParams],
        queryFn: () =>
            fetchCampaignRefunds(ntafId, page, {
                from: filters.from || undefined,
                to: filters.to || undefined,
                search: filters.search || undefined,
                ...sortParams,
            }),
        enabled: !!ntafId,
        staleTime: Infinity,
    });

    const count = data?.count ?? 0;

    return (
        <RefundsTable
            rows={data?.results ?? []}
            count={count}
            page={page}
            setPage={setPage}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            isLoading={isLoading}
            isError={isError}
            showStatus
            emptyMessage='No refunds recorded for this campaign.'
            errorMessage='Could not load refunds for this campaign.'
            summary={
                <p>
                    {count
                        ? `Showing ${Math.min(
                              REFUNDS_PAGE_SIZE,
                              count - (page - 1) * REFUNDS_PAGE_SIZE
                          )} of ${count} refunds`
                        : 'No refunds for this campaign'}
                </p>
            }
        />
    );
};

export default RefundedDonationsTable;
